import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ChevronDown, ChevronUp, Trash2 } from "lucide-react";
import { getNotificationIcon, formatRelativeTime, resolveNotificationRoute } from "../../utils/notificationHelpers";
import { color, radius } from "../../styles/theme";

/**
 * One row in the full notification list — expand for the full
 * description + absolute timestamp, or collapsed for a 2-line preview.
 * Mirrors mobile's NotificationCell (icon-by-category, unread highlight,
 * swipe-to-delete → here a plain delete button since there's no swipe
 * gesture on the web).
 */
const NotificationRow = ({ item, onMarkRead, onDelete }) => {
  const { t } = useTranslation("notifications");
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState(false);
  const Icon = getNotificationIcon(item.Notification?.CategoryName);

  const handleBodyClick = () => {
    if (!item.IsRead) onMarkRead(item.Id);
    const route = resolveNotificationRoute(item);
    if (route) navigate(route);
  };

  return (
    <div style={{ ...s.row, ...(item.IsRead ? {} : s.rowUnread) }}>
      <span style={s.icon}><Icon size={16} strokeWidth={2} /></span>

      <div style={s.body} onClick={handleBodyClick}>
        <div style={s.topLine}>
          <span style={{ ...s.title, ...(item.IsRead ? {} : s.titleUnread) }}>{item.Notification?.Title}</span>
          {!item.IsRead && <span style={s.unreadDot} />}
        </div>
        <p style={{ ...s.desc, WebkitLineClamp: expanded ? "unset" : 2 }}>{item.Notification?.Description}</p>
        <span style={s.time}>
          {expanded
            ? new Date(item.Notification?.CreationDate).toLocaleString()
            : formatRelativeTime(item.Notification?.CreationDate, t)}
        </span>
      </div>

      <button type="button" className="btn-ghost" style={s.iconBtn} onClick={() => setExpanded((v) => !v)} aria-label={t("row.expand")}>
        {expanded ? <ChevronUp size={15} strokeWidth={2} /> : <ChevronDown size={15} strokeWidth={2} />}
      </button>
      <button type="button" className="btn-ghost" style={{ ...s.iconBtn, color: color.danger500 }} onClick={() => onDelete(item.Id)} aria-label={t("row.delete")}>
        <Trash2 size={15} strokeWidth={2} />
      </button>
    </div>
  );
};

const s = {
  row: {
    display: "flex", alignItems: "flex-start", gap: 12, padding: "14px 16px",
    borderBottom: `1px solid ${color.border}`, background: color.surface,
  },
  rowUnread: { background: color.primary50 },
  icon: {
    width: 34, height: 34, borderRadius: radius.sm, background: color.gray100, color: color.gray700,
    display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, marginTop: 2,
  },
  body: { flex: 1, minWidth: 0, cursor: "pointer" },
  topLine: { display: "flex", alignItems: "center", gap: 7 },
  title: { fontSize: 13.5, fontWeight: 500, color: color.gray700 },
  titleUnread: { fontWeight: 700, color: color.textPrimary },
  unreadDot: { width: 7, height: 7, borderRadius: "50%", background: color.primary500, flexShrink: 0 },
  desc: {
    margin: "4px 0 0", fontSize: 12.5, color: color.textSecondary, lineHeight: 1.5,
    overflow: "hidden", display: "-webkit-box", WebkitBoxOrient: "vertical",
  },
  time: { display: "block", marginTop: 5, fontSize: 11, color: color.textMuted },
  iconBtn: { width: 30, height: 30, padding: 0, borderRadius: radius.sm, flexShrink: 0 },
};

export default NotificationRow;
